import { useState, useRef, useEffect, useCallback } from "react";

const AGENTS = {
  architect: {
    id: "architect", name: "Architect", emoji: "🏗️", color: "#4FC3F7", shortColor: "#1a3a4a",
    systemPrompt: `You are @architect in a multi-agent AI chat room. Your role: system design, high-level technical decisions, structuring solutions.
Other agents: @developer (writes code), @critic (reviews & challenges), @pm (tracks tasks), @researcher (gathers info).
Rules:
- Keep responses focused and concise (3-5 sentences max unless technical depth needed)
- You CAN and SHOULD tag other agents when appropriate (they will auto-respond)
- Tag @developer when implementation is needed, @critic for design review, @pm to track tasks, @researcher for context
- You can be tagged by other agents and must respond in role
- Format: speak naturally as the Architect. Don't repeat the chat history back.`
  },
  developer: {
    id: "developer", name: "Developer", emoji: "💻", color: "#A5D6A7", shortColor: "#1a3a1a",
    systemPrompt: `You are @developer in a multi-agent AI chat room. Your role: write code, implement features, solve technical problems.
Other agents: @architect (designs), @critic (reviews), @pm (tracks tasks), @researcher (gathers info).
Rules:
- Keep responses concise. For code, write short focused snippets only when truly needed.
- You CAN and SHOULD tag other agents when appropriate (they will auto-respond)
- Tag @architect for design questions, @critic for code review, @pm to log completed work
- You can be tagged by other agents and must respond in role
- Format: speak naturally as the Developer. Don't repeat the chat history back.`
  },
  critic: {
    id: "critic", name: "Critic", emoji: "🔍", color: "#FFCC80", shortColor: "#3a2a0a",
    systemPrompt: `You are @critic in a multi-agent AI chat room. Your role: challenge assumptions, find flaws, improve quality. Be direct but constructive.
Other agents: @architect (designs), @developer (codes), @pm (tracks), @researcher (researches).
Rules:
- Keep reviews focused and actionable (3-5 points max)
- You CAN and SHOULD tag agents to act on your feedback
- Don't just criticize — suggest the fix and tag whoever should handle it
- You can be tagged by other agents and must respond in role
- Format: speak naturally as the Critic. Don't repeat the chat history back.`
  },
  pm: {
    id: "pm", name: "PM", emoji: "📋", color: "#CE93D8", shortColor: "#2a0a3a",
    systemPrompt: `You are @pm (Project Manager) in a multi-agent AI chat room. Your role: track tasks, assign work, set priorities, keep the team organized.
Other agents: @architect (designs), @developer (codes), @critic (reviews), @researcher (researches).
Rules:
- Keep messages short and action-oriented
- Use ✅ for completed tasks, 🔲 for open tasks, 🔥 for blockers
- You CAN tag agents to delegate or unblock work
- You can be tagged by other agents and must respond in role
- Format: speak naturally as the PM. Don't repeat the chat history back.`
  },
  researcher: {
    id: "researcher", name: "Researcher", emoji: "🔬", color: "#F48FB1", shortColor: "#3a0a1a",
    systemPrompt: `You are @researcher in a multi-agent AI chat room. Your role: gather context, surface edge cases, provide background knowledge and tradeoffs.
Other agents: @architect (designs), @developer (codes), @critic (reviews), @pm (tracks).
Rules:
- Be informative but brief — key findings only (3-5 points)
- You CAN tag agents to act on your findings
- You can be tagged by other agents and must respond in role
- Format: speak naturally as the Researcher. Don't repeat the chat history back.`
  }
};

const AGENT_ORDER = ["architect", "developer", "critic", "pm", "researcher"];
const MAX_AUTO_TURNS = 10;
const AGENT_DELAY = 1400;

const MENTION_REGEX = /@(architect|developer|critic|pm|researcher)/gi;

function extractMentions(text) {
  const found = [];
  const matches = [...text.matchAll(MENTION_REGEX)];
  for (const m of matches) {
    const id = m[1].toLowerCase();
    if (!found.includes(id)) found.push(id);
  }
  return found;
}

function formatConversation(messages) {
  return messages
    .filter(m => m.role !== "system")
    .map(m => {
      if (m.role === "user") return `[Human]: ${m.text}`;
      if (m.role === "agent") return `[@${m.agentId}/${m.agentName}]: ${m.text}`;
      return m.text;
    })
    .join("\n\n");
}

function RenderText({ text }) {
  const parts = text.split(/(@(?:architect|developer|critic|pm|researcher))/gi);
  return (
    <span>
      {parts.map((part, i) => {
        const lower = part.toLowerCase();
        if (lower.startsWith("@") && AGENTS[lower.slice(1)]) {
          const agent = AGENTS[lower.slice(1)];
          return (
            <span key={i} style={{
              color: agent.color,
              fontWeight: 700,
              background: agent.shortColor,
              padding: "0 4px",
              borderRadius: 3,
              fontSize: "0.9em"
            }}>{part}</span>
          );
        }
        // Format code blocks inline
        if (part.startsWith("```") || part.endsWith("```")) {
          return <span key={i} style={{ color: "#78909C" }}>{part}</span>;
        }
        return <span key={i}>{part}</span>;
      })}
    </span>
  );
}

function AgentTag({ agentId }) {
  const a = AGENTS[agentId];
  if (!a) return null;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      background: a.shortColor, color: a.color,
      border: `1px solid ${a.color}33`,
      borderRadius: 4, padding: "1px 7px", fontSize: "0.72rem",
      fontWeight: 700, letterSpacing: "0.04em", cursor: "default",
      userSelect: "none"
    }}>
      {a.emoji} @{agentId}
    </span>
  );
}

export default function AgentChatRoom() {
  const [messages, setMessages] = useState([
    {
      id: "init", role: "system",
      text: "Multi-agent room online. Tag an agent to start: @architect @developer @critic @pm @researcher — they can tag each other too.",
      ts: Date.now()
    }
  ]);
  const [input, setInput] = useState("");
  const [isRunning, setIsRunning] = useState(false);
  const [typingAgent, setTypingAgent] = useState(null);
  const [turnCount, setTurnCount] = useState(0);
  const [activePanel, setActivePanel] = useState("agents");

  const messagesRef = useRef(messages);
  const loopActiveRef = useRef(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { messagesRef.current = messages; }, [messages]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, typingAgent]);

  const addMessage = useCallback((msg) => {
    const newMsg = { id: `${Date.now()}-${Math.random()}`, ts: Date.now(), ...msg };
    setMessages(prev => {
      const updated = [...prev, newMsg];
      messagesRef.current = updated;
      return updated;
    });
    return newMsg;
  }, []);

  const callAgent = async (agentId, conversationHistory) => {
    const agent = AGENTS[agentId];
    const transcript = formatConversation(conversationHistory);
    const prompt = `Current chat room transcript:\n\n${transcript}\n\n---\nYou were just tagged. Respond as @${agentId} now.`;

    const response = await fetch("https://api.anthropic.com/v1/messages", {
      method: "POST",
      headers: {"Content-Type": "application/json",
"x-api-key": process.env.REACT_APP_ANTHROPIC_KEY,
"anthropic-version": "2023-06-01",
"anthropic-dangerous-direct-browser-access": "true", },
      body: JSON.stringify({
        model: "claude-sonnet-4-20250514",
        max_tokens: 1000,
        system: agent.systemPrompt,
        messages: [{ role: "user", content: prompt }]
      })
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data.content?.[0]?.text?.trim() || "";
  }

  const runAgentLoop = async (firstAgentId, startMessages) => {
    loopActiveRef.current = true;
    let currentMessages = startMessages;
    let agentId = firstAgentId;
    let depth = 0;

    while (depth < MAX_AUTO_TURNS && loopActiveRef.current) {
      setTypingAgent(agentId);
      setTurnCount(depth + 1);

      try {
        const responseText = await callAgent(agentId, currentMessages);
        if (!loopActiveRef.current) break;

        const newMsg = {
          id: `${Date.now()}-${Math.random()}`,
          role: "agent",
          agentId,
          agentName: AGENTS[agentId].name,
          text: responseText,
          ts: Date.now(),
          turn: depth + 1
        };

        setMessages(prev => {
          const updated = [...prev, newMsg];
          messagesRef.current = updated;
          return updated;
        });
        currentMessages = [...currentMessages, newMsg];

        const mentions = extractMentions(responseText);
        if (mentions.length === 0 || depth + 1 >= MAX_AUTO_TURNS) break;

        agentId = mentions[0];
        depth++;
        await new Promise(r => setTimeout(r, AGENT_DELAY));
      } catch (err) {
        addMessage({ role: "system", text: `⚠ Error: ${err.message}` });
        break;
      }
    }

    setTypingAgent(null);
    setIsRunning(false);
    loopActiveRef.current = false;
    setTurnCount(0);
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || isRunning) return;

    setInput("");
    const mentions = extractMentions(text);

    const userMsg = {
      id: `${Date.now()}-u`, role: "user", text, ts: Date.now()
    };
    const newMessages = [...messagesRef.current, userMsg];
    setMessages(newMessages);
    messagesRef.current = newMessages;

    if (mentions.length === 0) {
      addMessage({ role: "system", text: "No agent tagged. Use @architect, @developer, @critic, @pm, or @researcher" });
      return;
    }

    setIsRunning(true);
    await runAgentLoop(mentions[0], newMessages);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleStop = () => {
    loopActiveRef.current = false;
    setTypingAgent(null);
    setIsRunning(false);
    setTurnCount(0);
    addMessage({ role: "system", text: "⏹ Loop stopped by user." });
  };

  const insertMention = (agentId) => {
    const mention = `@${agentId} `;
    setInput(prev => {
      const trimmed = prev.trimEnd();
      return trimmed ? `${trimmed} ${mention}` : mention;
    });
    inputRef.current?.focus();
  };

  const clearChat = () => {
    setMessages([{ id: "init2", role: "system", text: "Chat cleared. Room reset.", ts: Date.now() }]);
    loopActiveRef.current = false;
    setIsRunning(false);
    setTypingAgent(null);
  };

  // Stats
  const agentMessageCounts = AGENT_ORDER.reduce((acc, id) => {
    acc[id] = messages.filter(m => m.agentId === id).length;
    return acc;
  }, {});
  const totalAgentTurns = Object.values(agentMessageCounts).reduce((a, b) => a + b, 0);

  return (
    <div style={{
      fontFamily: "'IBM Plex Mono', 'Fira Code', 'Cascadia Code', monospace",
      background: "#05080f",
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      color: "#8899aa"
    }}>
      {/* Scanline overlay */}
      <div style={{
        position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0,
        backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.03) 2px, rgba(0,0,0,0.03) 4px)",
      }} />

      {/* Header */}
      <div style={{
        borderBottom: "1px solid #111d2e",
        padding: "12px 20px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: "#060a12", position: "sticky", top: 0, zIndex: 10,
        gap: 12
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ display: "flex", gap: 5 }}>
            {["#ff5f57","#febc2e","#28c840"].map((c, i) => (
              <div key={i} style={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
            ))}
          </div>
          <span style={{ color: "#4FC3F7", fontSize: "0.8rem", fontWeight: 700, letterSpacing: "0.12em" }}>
            AGENT·ROOM
          </span>
          <span style={{ color: "#1e3a5f", fontSize: "0.7rem" }}>v0.1.0</span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 16, fontSize: "0.7rem" }}>
          {isRunning && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#A5D6A7" }}>
              <span style={{ animation: "pulse 1s infinite", display: "inline-block", color: "#4CAF50" }}>●</span>
              {typingAgent && <AgentTag agentId={typingAgent} />}
              <span style={{ color: "#556677" }}>turn {turnCount}/{MAX_AUTO_TURNS}</span>
            </div>
          )}
          <span style={{ color: "#1e3a5f" }}>{totalAgentTurns} agent turns</span>
          <button
            onClick={clearChat}
            style={{ background: "none", border: "1px solid #111d2e", color: "#445566", cursor: "pointer",
              padding: "3px 10px", borderRadius: 4, fontSize: "0.7rem", fontFamily: "inherit" }}
          >clear</button>
        </div>
      </div>

      <div style={{ display: "flex", flex: 1, overflow: "hidden", position: "relative", zIndex: 1 }}>
        {/* Sidebar */}
        <div style={{
          width: 200, borderRight: "1px solid #0d1a28",
          background: "#060a12", padding: "16px 12px",
          display: "flex", flexDirection: "column", gap: 20,
          flexShrink: 0
        }}>
          {/* Tab switcher */}
          <div style={{ display: "flex", gap: 0, borderBottom: "1px solid #0d1a28", paddingBottom: 8 }}>
            {["agents", "stats"].map(tab => (
              <button key={tab} onClick={() => setActivePanel(tab)}
                style={{
                  flex: 1, background: "none", border: "none", cursor: "pointer",
                  color: activePanel === tab ? "#4FC3F7" : "#334455",
                  fontFamily: "inherit", fontSize: "0.65rem", fontWeight: 700,
                  letterSpacing: "0.08em", padding: "4px 0",
                  borderBottom: activePanel === tab ? "1px solid #4FC3F7" : "1px solid transparent",
                  marginBottom: -1
                }}>{tab.toUpperCase()}</button>
            ))}
          </div>

          {activePanel === "agents" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ fontSize: "0.62rem", color: "#223344", letterSpacing: "0.1em", marginBottom: 4 }}>
                CLICK TO TAG
              </div>
              {AGENT_ORDER.map(id => {
                const a = AGENTS[id];
                const isTyping = typingAgent === id;
                return (
                  <button key={id} onClick={() => insertMention(id)}
                    style={{
                      display: "flex", alignItems: "center", gap: 8,
                      background: isTyping ? a.shortColor : "transparent",
                      border: `1px solid ${isTyping ? a.color + "44" : "#0d1a28"}`,
                      borderRadius: 6, padding: "7px 10px", cursor: "pointer",
                      color: isTyping ? a.color : "#556677",
                      fontFamily: "inherit", fontSize: "0.72rem",
                      transition: "all 0.15s", width: "100%", textAlign: "left"
                    }}>
                    <span style={{ fontSize: "0.85rem" }}>{a.emoji}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: isTyping ? a.color : "#667788", fontWeight: 700, fontSize: "0.68rem" }}>
                        @{id}
                      </div>
                    </div>
                    {isTyping && (
                      <span style={{ color: a.color, fontSize: "0.6rem", animation: "blink 1s infinite" }}>▊</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {activePanel === "stats" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ fontSize: "0.62rem", color: "#223344", letterSpacing: "0.1em" }}>AGENT ACTIVITY</div>
              {AGENT_ORDER.map(id => {
                const a = AGENTS[id];
                const count = agentMessageCounts[id];
                const pct = totalAgentTurns ? Math.round((count / totalAgentTurns) * 100) : 0;
                return (
                  <div key={id}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.65rem", marginBottom: 3 }}>
                      <span style={{ color: a.color }}>{a.emoji} @{id}</span>
                      <span style={{ color: "#334455" }}>{count}</span>
                    </div>
                    <div style={{ background: "#0a1520", borderRadius: 3, height: 3, overflow: "hidden" }}>
                      <div style={{ width: `${pct}%`, height: "100%", background: a.color, borderRadius: 3, transition: "width 0.4s" }} />
                    </div>
                  </div>
                );
              })}
              <div style={{ borderTop: "1px solid #0d1a28", paddingTop: 10, marginTop: 4 }}>
                <div style={{ fontSize: "0.62rem", color: "#223344", letterSpacing: "0.1em", marginBottom: 6 }}>TOTALS</div>
                <div style={{ fontSize: "0.68rem", color: "#445566" }}>
                  <div>{messages.filter(m => m.role === "user").length} user msgs</div>
                  <div>{totalAgentTurns} agent turns</div>
                  <div>{messages.length} total</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Chat area */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px", display: "flex", flexDirection: "column", gap: 2 }}>
            {messages.map((msg, idx) => {
              if (msg.role === "system") {
                return (
                  <div key={msg.id} style={{
                    fontSize: "0.68rem", color: "#223344", textAlign: "center",
                    padding: "8px 0", borderTop: idx > 0 ? "none" : undefined
                  }}>
                    ── {msg.text} ──
                  </div>
                );
              }

              if (msg.role === "user") {
                return (
                  <div key={msg.id} style={{
                    display: "flex", flexDirection: "column", alignItems: "flex-end", margin: "8px 0"
                  }}>
                    <div style={{ fontSize: "0.62rem", color: "#223344", marginBottom: 4 }}>
                      you · {new Date(msg.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </div>
                    <div style={{
                      background: "#0d1a2a", border: "1px solid #162030",
                      borderRadius: "10px 10px 3px 10px",
                      padding: "10px 14px", maxWidth: "72%", fontSize: "0.82rem",
                      lineHeight: 1.6, color: "#aabbcc"
                    }}>
                      <RenderText text={msg.text} />
                    </div>
                  </div>
                );
              }

              if (msg.role === "agent") {
                const agent = AGENTS[msg.agentId];
                if (!agent) return null;
                return (
                  <div key={msg.id} style={{ margin: "8px 0" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                      <span style={{ fontSize: "0.9rem" }}>{agent.emoji}</span>
                      <span style={{ fontSize: "0.68rem", color: agent.color, fontWeight: 700 }}>@{msg.agentId}</span>
                      <span style={{ fontSize: "0.6rem", color: "#1e3040" }}>
                        {new Date(msg.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </span>
                      {msg.turn && (
                        <span style={{
                          fontSize: "0.58rem", color: "#1e3040",
                          border: "1px solid #0d1a28", borderRadius: 3,
                          padding: "1px 5px"
                        }}>turn {msg.turn}</span>
                      )}
                    </div>
                    <div style={{
                      background: agent.shortColor,
                      border: `1px solid ${agent.color}22`,
                      borderLeft: `2px solid ${agent.color}`,
                      borderRadius: "0 10px 10px 10px",
                      padding: "10px 14px", maxWidth: "85%",
                      fontSize: "0.82rem", lineHeight: 1.7,
                      color: "#9aabb8", whiteSpace: "pre-wrap", wordBreak: "break-word"
                    }}>
                      <RenderText text={msg.text} />
                    </div>
                  </div>
                );
              }
              return null;
            })}

            {/* Typing indicator */}
            {typingAgent && (
              <div style={{ margin: "8px 0" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                  <span style={{ fontSize: "0.9rem" }}>{AGENTS[typingAgent]?.emoji}</span>
                  <span style={{ fontSize: "0.68rem", color: AGENTS[typingAgent]?.color, fontWeight: 700 }}>
                    @{typingAgent}
                  </span>
                  <span style={{ fontSize: "0.6rem", color: "#334455" }}>thinking...</span>
                </div>
                <div style={{
                  background: AGENTS[typingAgent]?.shortColor,
                  border: `1px solid ${AGENTS[typingAgent]?.color}22`,
                  borderLeft: `2px solid ${AGENTS[typingAgent]?.color}`,
                  borderRadius: "0 10px 10px 10px",
                  padding: "12px 16px", display: "inline-flex", gap: 5, alignItems: "center"
                }}>
                  {[0,1,2].map(i => (
                    <span key={i} style={{
                      width: 6, height: 6, borderRadius: "50%",
                      background: AGENTS[typingAgent]?.color,
                      opacity: 0.6,
                      animation: `bounce 1.2s ${i * 0.2}s infinite`
                    }} />
                  ))}
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input area */}
          <div style={{
            borderTop: "1px solid #0d1a28",
            padding: "12px 16px",
            background: "#060a12",
            display: "flex", flexDirection: "column", gap: 8
          }}>
            {/* Quick tag row */}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {AGENT_ORDER.map(id => (
                <button key={id} onClick={() => insertMention(id)}
                  disabled={isRunning}
                  style={{
                    background: "none",
                    border: `1px solid ${AGENTS[id].color}33`,
                    color: AGENTS[id].color,
                    borderRadius: 4, padding: "2px 8px",
                    fontSize: "0.65rem", cursor: isRunning ? "not-allowed" : "pointer",
                    fontFamily: "inherit", opacity: isRunning ? 0.4 : 0.8,
                    transition: "opacity 0.15s"
                  }}>
                  {AGENTS[id].emoji} @{id}
                </button>
              ))}
            </div>

            <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isRunning}
                placeholder="Tag an agent to start... e.g. @architect design a REST API for a todo app"
                rows={2}
                style={{
                  flex: 1, background: "#0a1520", border: "1px solid #0d1a28",
                  borderRadius: 8, padding: "10px 12px",
                  color: "#99aabb", fontFamily: "inherit", fontSize: "0.82rem",
                  resize: "none", outline: "none", lineHeight: 1.5,
                  opacity: isRunning ? 0.5 : 1
                }}
              />
              {isRunning ? (
                <button onClick={handleStop}
                  style={{
                    background: "#3a1010", border: "1px solid #ff444444",
                    color: "#ff6666", borderRadius: 8, padding: "10px 16px",
                    cursor: "pointer", fontFamily: "inherit", fontSize: "0.75rem",
                    fontWeight: 700, whiteSpace: "nowrap"
                  }}>
                  ⏹ stop
                </button>
              ) : (
                <button onClick={handleSend}
                  disabled={!input.trim()}
                  style={{
                    background: input.trim() ? "#0d2a4a" : "#080f18",
                    border: `1px solid ${input.trim() ? "#4FC3F744" : "#0d1a28"}`,
                    color: input.trim() ? "#4FC3F7" : "#223344",
                    borderRadius: 8, padding: "10px 16px",
                    cursor: input.trim() ? "pointer" : "not-allowed",
                    fontFamily: "inherit", fontSize: "0.75rem",
                    fontWeight: 700, transition: "all 0.15s"
                  }}>
                  send ↵
                </button>
              )}
            </div>
            <div style={{ fontSize: "0.6rem", color: "#162030" }}>
              enter to send · shift+enter for newline · agents auto-loop up to {MAX_AUTO_TURNS} turns
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;700&display=swap');
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }
        @keyframes bounce { 0%,80%,100%{transform:translateY(0)} 40%{transform:translateY(-6px)} }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: #05080f; }
        ::-webkit-scrollbar-thumb { background: #0d1a28; border-radius: 4px; }
        textarea::placeholder { color: #1e3040; }
      `}</style>
    </div>
  );
}